import {
  AuthSignIn,
  startAuthSessionHandoff,
  useAuthState,
  waitForAuthSession,
} from '@origens/auth'
import { OrigensLogo } from '@origens/ui'
import { useEffect, useState } from 'react'
import { publishableKey } from '../lib/auth-env.js'

export function SignInLoadingState() {
  return (
    <div className="auth-card">
      <OrigensLogo className="auth-logo" />
      <p className="auth-subtitle">Signing you in&hellip;</p>
    </div>
  )
}

function SignInHandoffError({ onRetry }: { onRetry: () => void }) {
  return (
    <div className="auth-card">
      <OrigensLogo className="auth-logo" />
      <p className="auth-subtitle">
        Your sign-in succeeded, but the session is still being finalized.
      </p>
      <button type="button" className="auth-retry" onClick={onRetry}>
        Try again
      </button>
    </div>
  )
}

function safeReturnTo(): string {
  if (typeof window === 'undefined') {
    return '/'
  }
  const raw = new URLSearchParams(window.location.search).get('redirect_url')
  if (!raw || !raw.startsWith('/') || raw.startsWith('//')) {
    return '/'
  }
  return raw
}

function handoffRedirectUrl(): string {
  const params = new URLSearchParams({
    redirect_url: safeReturnTo(),
    auth_complete: '1',
  })
  return `/sign-in?${params.toString()}`
}

export function SignInWidget() {
  const { isLoaded, isSignedIn, getToken } = useAuthState()
  const [handoffState, setHandoffState] = useState<'idle' | 'waiting' | 'failed'>('idle')
  const [handoffAttempt, setHandoffAttempt] = useState(0)

  useEffect(() => {
    if (!isLoaded || !isSignedIn) {
      return
    }
    setHandoffState('waiting')

    return startAuthSessionHandoff({
      waitForSession: () =>
        waitForAuthSession({
          getToken,
          // Probe the protected document route without following its redirect.
          // This confirms that the Go middleware can verify the fresh __session
          // cookie before the browser performs the real document navigation.
          probe: async () => {
            const response = await fetch('/', {
              method: 'HEAD',
              credentials: 'same-origin',
              redirect: 'manual',
              cache: 'no-store',
            })
            return response.ok
          },
        }),
      onReady: () => {
        window.location.replace(safeReturnTo())
      },
      onFailed: () => setHandoffState('failed'),
    })
    // handoffState is intentionally not a dependency: setting it to waiting
    // must not clean up and cancel the handoff this effect just started.
  }, [getToken, handoffAttempt, isLoaded, isSignedIn])

  if (!publishableKey) {
    return (
      <p className="auth-subtitle">
        Sign-in is not configured for this environment.
      </p>
    )
  }

  if (!isLoaded || handoffState === 'waiting') {
    return <SignInLoadingState />
  }

  if (handoffState === 'failed') {
    return <SignInHandoffError onRetry={() => setHandoffAttempt((attempt) => attempt + 1)} />
  }

  if (isSignedIn) return <SignInLoadingState />

  // Keep the sign-in widget mounted for the whole unsigned flow. Gating it
  // behind a signed-out boundary can unmount it after password submit (the
  // leaves both SignedIn/SignedOut empty), which left a blank page at
  // #/factor-one.
  return (
    <AuthSignIn
      routing="hash"
      withSignUp={false}
      transferable={false}
      forceRedirectUrl={handoffRedirectUrl()}
      fallbackRedirectUrl={handoffRedirectUrl()}
    />
  )
}
